import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const COMPLIANCE_LABELS = { hipaa: 'HIPAA', soc2: 'SOC 2', pci: 'PCI DSS', cmmc: 'CMMC' };

// v3.5.86.1: STANDARD_EXCLUSIONS is universal across quote types — these
// are MSP-scope exclusions that apply equally to Managed IT, Multi-Site,
// Bundle, and (when wired up in v3.5.86.4) Voice. Some entries reference
// IT-flavored examples but the underlying scope ("custom software dev",
// "compliance audits", "after-hours emergency") applies to any MSP engagement.
const STANDARD_EXCLUSIONS = [
  'Custom software development, advanced scripting, or specialized automation beyond standard platform configurations',
  'Complex third-party software integrations or application-to-application customizations',
  'One-time or large-scale initiatives such as infrastructure redesigns, major migrations, office expansions, or technology refresh projects',
  'Deep application-level support for specialized or line-of-business software not covered by standard vendor support',
  'Hardware procurement, physical installation, cabling, or on-site build-out services unless specifically included',
  'Support for legacy, end-of-life, or non-standard systems that fall outside recommended best practices',
  'Formal compliance audits, legal advisory services, or certification preparation',
  'Advanced incident response, digital forensics, or remediation services beyond standard monitoring and response capabilities',
  'User training, security awareness programs, or policy development beyond what is included in the selected service tier',
  'On-site, after-hours, or emergency services outside defined support hours and service level agreements',
];

// v3.5.86.1: Default deliverables text per quote type. Registry pattern lets
// each quoteType bring its own pre-populated text without conditional sprawl
// in the render. Voice has a separate VoiceAssumptionsModal so 'voice' is
// intentionally not in this map. multi_site populated in v3.5.86.2; bundle
// entry filled in by v3.5.86.3.
const STANDARD_DELIVERABLES_IT = `Ferrum Technology Services, LLC will provide standardized onboarding and ongoing management of workstations, laptops, servers, network infrastructure, Microsoft 365, and associated cloud and on-premises environments in alignment with Ferrum Technology Services, LLC's established operational, security, and compliance standards.

Deliverables include the deployment and configuration of core management, monitoring, and security platforms as applicable to the selected service tier. This may include remote monitoring and management (RMM), endpoint detection and incident response (EDR/EDIR), managed detection and response (MDR), security operations center (SOC) monitoring, DNS filtering, and Microsoft 365 security baseline hardening.

As part of onboarding, Ferrum Technology Services, LLC will establish and maintain updated technical documentation, including but not limited to network diagrams, firewall configurations, Azure/Microsoft 365 tenant settings, and credential management practices.`;

// v3.5.86.2: Multi-Site default deliverables. Mirrors the IT text but adds
// per-location and inter-site language so the prospect understands the
// engagement covers each site uniformly under one agreement.
const STANDARD_DELIVERABLES_MULTI_SITE = `Ferrum Technology Services, LLC will provide standardized onboarding and ongoing management across all locations covered by this engagement, including workstations, laptops, servers, network infrastructure, Microsoft 365, and associated cloud and on-premises environments at each site, in alignment with Ferrum Technology Services, LLC's established operational, security, and compliance standards.

Deliverables include the deployment and configuration of core management, monitoring, and security platforms as applicable to the selected service tier — applied uniformly across all locations under this agreement. This may include remote monitoring and management (RMM), endpoint detection and incident response (EDR/EDIR), managed detection and response (MDR), security operations center (SOC) monitoring, DNS filtering, and Microsoft 365 security baseline hardening.

As part of onboarding, Ferrum Technology Services, LLC will establish and maintain updated technical documentation per location, including but not limited to per-site network diagrams, firewall configurations, inter-site connectivity topology, Azure/Microsoft 365 tenant settings, and credential management practices.`;

// v3.5.86.3: Bundle (IT + Voice) default deliverables. Mirrors the IT
// foundation text and adds a voice-specific paragraph covering hosted PBX,
// SIP trunking, handsets, number porting, and call quality / QoS so the
// prospect understands the bundle covers both surfaces under one agreement
// and one bill. Free-phones language is conditional language the rep can
// edit out for non-qualifying engagements.
const STANDARD_DELIVERABLES_BUNDLE = `Ferrum Technology Services, LLC will provide standardized onboarding and ongoing management of workstations, laptops, servers, network infrastructure, Microsoft 365, and associated cloud and on-premises environments in alignment with Ferrum Technology Services, LLC's established operational, security, and compliance standards.

Deliverables include the deployment and configuration of core management, monitoring, and security platforms as applicable to the selected service tier. This may include remote monitoring and management (RMM), endpoint detection and incident response (EDR/EDIR), managed detection and response (MDR), security operations center (SOC) monitoring, DNS filtering, and Microsoft 365 security baseline hardening.

In addition to managed IT, Ferrum Technology Services, LLC will provide hosted voice services on the 3CX platform, including SIP trunking, hosted PBX configuration, user / extension provisioning, voicemail, auto-attendant configuration where applicable, and ongoing platform administration. Number porting from the existing voice provider will be coordinated as part of onboarding when in scope. Call quality and Quality of Service (QoS) considerations on the client's network will be reviewed during onboarding; remediation of underlying network deficiencies (when identified) is scoped separately.

As part of onboarding, Ferrum Technology Services, LLC will establish and maintain updated technical documentation, including but not limited to network diagrams, firewall configurations, Azure/Microsoft 365 tenant settings, voice routing configuration, and credential management practices.`;

const DELIVERABLES_BY_TYPE = {
  it:         STANDARD_DELIVERABLES_IT,
  multi_site: STANDARD_DELIVERABLES_MULTI_SITE,
  bundle:     STANDARD_DELIVERABLES_BUNDLE,
};

// Back-compat alias — older callers (and the load-state branch below) read
// from STANDARD_DELIVERABLES. Keep it pointing at the IT default since IT
// is currently the only mounted consumer.
const STANDARD_DELIVERABLES = STANDARD_DELIVERABLES_IT;

// v3.5.86.1: AssumptionsModal is the shared assumptions/exclusions capture
// component, parameterized by `quoteType`. The IT QuotePage was the original
// (and currently sole) consumer; v3.5.86.2 mounts on MultiSiteQuotePage with
// a per-location AI prompt, v3.5.86.3 mounts on BundleQuotePage with a
// combined IT+Voice AI prompt, and v3.5.86.4 enhances VoiceAssumptionsModal
// (which stays a separate file — voice has divergent capture needs that
// don't compose cleanly into this shell).
//
// quoteType defaults to 'it' so any caller that hasn't been updated yet
// gets the previous behavior verbatim. IT-specific UI sections render
// only when quoteType === 'it'; all other sections (header, customer story,
// AI generation, custom exclusions, additional notes, save) are universal.
//
// The AI payload is built by buildAIPayload(quoteType, state, props) — IT
// is the only working case in this deploy; multi_site / bundle throw a
// pointed "not yet wired up" error that names the upcoming deploy slot,
// to make the error trail clear if someone wires up a mount before .2/.3.
export default function AssumptionsModal({
  onClose, quoteId, quoteNumber, clientName, recipientContact,
  inputs, pkg, products, settings, complianceKey, onSave,
  quoteType = 'it',
}) {
  const selectedProductObjects = (inputs?.selectedProducts || [])
    .map(id => products?.find(p => p.id === id)).filter(Boolean);

  // Resolve the default deliverables text for this quote type. IT has the
  // canonical text; multi_site and bundle are empty pending v3.5.86.2/.3.
  const defaultDeliverables = DELIVERABLES_BY_TYPE[quoteType] ?? '';

  // ── Discovery state ─────────────────────────────────────────────────────────
  const [story,            setStory]            = useState('');
  const [currentProvider,  setCurrentProvider]  = useState('');
  const [userList,         setUserList]         = useState('');
  const [fieldTechCount,   setFieldTechCount]   = useState('');
  const [fieldTechNotes,   setFieldTechNotes]   = useState('');
  const [remoteCount,      setRemoteCount]      = useState(0);
  const [remoteType,       setRemoteType]       = useState('hybrid');

  // Infrastructure
  const [serverNotes,      setServerNotes]      = useState('');
  const [networkNotes,     setNetworkNotes]     = useState('');
  const [telephonyNotes,   setTelephonyNotes]   = useState('');

  // Cloud & M365
  const [m365Managed,      setM365Managed]      = useState(false);
  const [m365Tier,         setM365Tier]         = useState('');
  const [m365Notes,        setM365Notes]        = useState('Email, Collaboration, Office applications, Identity services integrated with Active Directory');
  const [cloudNotes,       setCloudNotes]       = useState('');

  // Modernization opportunities
  const [modernizationNotes, setModernizationNotes] = useState('');

  // Deliverables & exclusions
  const [deliverables,     setDeliverables]     = useState(defaultDeliverables);
  const [customExclusions, setCustomExclusions] = useState([]);
  const [newExclusion,     setNewExclusion]     = useState('');
  const [customNotes,      setCustomNotes]      = useState('');

  const [saving,        setSaving]        = useState(false);
  const [generating,    setGenerating]    = useState(false);
  const [aiResult,      setAiResult]      = useState(null);
  const [switchDetails, setSwitchDetails] = useState('');
  const [laptopNotes,   setLaptopNotes]   = useState('');
  const [taskWorkerCount, setTaskWorkerCount] = useState('');
  const [saveMsg, setSaveMsg] = useState('');

  // v3.5.86.2: Multi-Site capture state. Only displayed when quoteType === 'multi_site'.
  // hqLocationId is the id of the location designated as headquarters (or '' for
  // fully-distributed). connectivityNotes describes inter-site topology (MPLS,
  // SD-WAN, separate ISPs, etc.). regionalNotes captures per-location
  // considerations like regional compliance variations or site-specific
  // operating hours.
  const [hqLocationId,     setHqLocationId]     = useState('');
  const [connectivityNotes, setConnectivityNotes] = useState('');
  const [regionalNotes,    setRegionalNotes]    = useState('');

  // v3.5.86.3: Bundle (IT + Voice) capture state. Only displayed when
  // quoteType === 'bundle'. The voice-side capture is intentionally lighter
  // than VoiceAssumptionsModal — seat counts, DIDs, fax requirements come
  // from the quote inputs themselves (passed via inputs.voiceSeats etc.),
  // so the modal only captures fields the quote engine doesn't already know:
  //   - Voice incumbent (often a separate carrier from the IT incumbent —
  //     "Spectrum Voice", "RingCentral", etc.). Distinguished from the
  //     universal currentProvider (which holds the IT incumbent).
  //   - Why switching voice (separate from why switching IT — different
  //     pain points often drive each decision)
  //   - Current PBX type (cloud / onprem / hybrid / none) — drives
  //     migration assumptions
  //   - Voice hardware notes (existing handset state, BYOH plans)
  //   - Voice network / QoS notes (bandwidth, VLAN segmentation, codec)
  //   - Number porting plan (full / partial / all-new, count of numbers)
  const [voiceCurrentProvider, setVoiceCurrentProvider] = useState('');
  const [voiceSwitchReasons,   setVoiceSwitchReasons]   = useState('');
  const [pbxType,              setPbxType]              = useState('');
  const [voiceHardwareNotes,   setVoiceHardwareNotes]   = useState('');
  const [voiceNetworkNotes,    setVoiceNetworkNotes]    = useState('');
  const [portingNotes,         setPortingNotes]         = useState('');

  // Load saved assumptions
  useEffect(() => {
    if (!quoteId) return;
    supabase.from('quotes').select('inputs').eq('id', quoteId).single()
      .then(({ data }) => {
        const a = data?.inputs?.assumptions;
        if (!a) return;
        setStory(a.story || '');
        setCurrentProvider(a.currentProvider || '');
        setUserList(a.userList || '');
        setFieldTechCount(a.fieldTechCount || '');
        setFieldTechNotes(a.fieldTechNotes || '');
        setRemoteCount(a.remoteCount || 0);
        setRemoteType(a.remoteType || 'hybrid');
        setServerNotes(a.serverNotes || '');
        setNetworkNotes(a.networkNotes || '');
        setTelephonyNotes(a.telephonyNotes || '');
        setM365Managed(a.m365Managed || false);
        setM365Tier(a.m365Tier || '');
        setM365Notes(a.m365Notes || 'Email, Collaboration, Office applications, Identity services integrated with Active Directory');
        setCloudNotes(a.cloudNotes || '');
        setModernizationNotes(a.modernizationNotes || '');
        setDeliverables(a.deliverables || defaultDeliverables);
        setCustomExclusions(a.customExclusions || []);
        setCustomNotes(a.customNotes || '');
        setSwitchDetails(a.switchDetails || '');
        setLaptopNotes(a.laptopNotes || '');
        setTaskWorkerCount(a.taskWorkerCount || '');
        // v3.5.86.2: hydrate multi-site capture fields. Empty defaults are safe
        // for IT consumers — these state vars exist regardless of quoteType but
        // their UI only renders when quoteType === 'multi_site'.
        setHqLocationId(a.hqLocationId || '');
        setConnectivityNotes(a.connectivityNotes || '');
        setRegionalNotes(a.regionalNotes || '');
        // v3.5.86.3: hydrate bundle voice capture fields. Same safety: empty
        // defaults for non-bundle quotes; UI only renders when quoteType === 'bundle'.
        setVoiceCurrentProvider(a.voiceCurrentProvider || '');
        setVoiceSwitchReasons(a.voiceSwitchReasons || '');
        setPbxType(a.pbxType || '');
        setVoiceHardwareNotes(a.voiceHardwareNotes || '');
        setVoiceNetworkNotes(a.voiceNetworkNotes || '');
        setPortingNotes(a.portingNotes || '');
        if (a.aiResult) setAiResult(a.aiResult);
      });
  }, [quoteId]);

  async function save() {
    if (!quoteId) { setSaveMsg('Save the quote first.'); return; }
    setSaving(true); setSaveMsg('');
    const assumptions = {
      story, currentProvider, switchDetails, userList,
      fieldTechCount: taskWorkerCount, fieldTechNotes, laptopNotes, taskWorkerCount,
      remoteCount, remoteType,
      serverNotes, networkNotes, telephonyNotes,
      m365Managed, m365Tier, m365Notes, cloudNotes,
      modernizationNotes, deliverables, aiResult: aiResult || null,
      customExclusions, customNotes,
      // v3.5.86.2: persist multi-site capture fields. Always written so save/
      // reload is symmetric; empty strings on IT quotes are harmless.
      hqLocationId, connectivityNotes, regionalNotes,
      // v3.5.86.3: persist bundle voice capture fields. Same shape — always
      // written, harmless on non-bundle quotes (just empty strings).
      voiceCurrentProvider, voiceSwitchReasons, pbxType,
      voiceHardwareNotes, voiceNetworkNotes, portingNotes,
      savedAt: new Date().toISOString(),
    };
    const currentInputs = (await supabase.from('quotes').select('inputs').eq('id', quoteId).single())?.data?.inputs || {};
    const { error } = await supabase.from('quotes')
      .update({ inputs: { ...currentInputs, assumptions } })
      .eq('id', quoteId);
    if (error) { setSaveMsg('✗ ' + error.message); }
    else { setSaveMsg('✓ Saved'); onSave?.(assumptions); setTimeout(() => setSaveMsg(''), 2500); }
    setSaving(false);
  }

  function addExclusion() {
    if (!newExclusion.trim()) return;
    setCustomExclusions(prev => [...prev, { id: `c_${Date.now()}`, text: newExclusion.trim() }]);
    setNewExclusion('');
  }

  async function generateAssumptions() {
    if (!story.trim()) return;
    setGenerating(true);
    try {
      // v3.5.86.1: payload construction extracted into buildAIPayload, keyed
      // by quoteType. IT case mirrors the v3.5.81.8 payload exactly. The
      // backend Netlify function (assumptionsAI) ignores the `quoteType`
      // field in this deploy — v3.5.86.2 will start routing on it when
      // Multi-Site lands.
      let payload;
      try {
        payload = buildAIPayload(quoteType, {
          story, switchDetails, currentProvider,
          serverNotes, networkNotes, laptopNotes, m365Notes, cloudNotes,
          remoteCount, remoteType, taskWorkerCount, fieldTechNotes,
          // v3.5.86.2: multi-site state. Universal-shape state object so the
          // payload builder can branch on quoteType without per-type call
          // sites diverging.
          hqLocationId, connectivityNotes, regionalNotes,
          // v3.5.86.3: bundle voice-side state. Same universal-shape pattern
          // — read by the bundle branch, ignored by the others.
          voiceCurrentProvider, voiceSwitchReasons, pbxType,
          voiceHardwareNotes, voiceNetworkNotes, portingNotes,
        }, { clientName, inputs, pkg, selectedProductObjects });
      } catch (payloadErr) {
        // Foundation guard: if a future caller mounts this modal with a
        // quoteType that isn't yet wired up server-side, surface the error
        // before we waste a Netlify function invocation. The error message
        // names the upcoming deploy slot so the next developer (or future
        // me) knows where the work lands.
        alert(payloadErr.message);
        setGenerating(false);
        return;
      }

      const res = await fetch('/.netlify/functions/assumptionsAI', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      // v3.5.81.24: defensive response parsing. Previously called
      // res.json() blindly, which throws "Unexpected token '<'" when
      // the server returns an HTML error page (Netlify timeout 504,
      // bad gateway 502, etc.). Read as text first, attempt parse,
      // produce a useful error message when the body isn't JSON.
      // Common case: Netlify function exceeded its 26s timeout — the
      // request might have actually succeeded but Netlify killed it
      // before the response body returned.
      const rawText = await res.text();
      let data;
      try {
        data = JSON.parse(rawText);
      } catch (parseErr) {
        if (res.status === 504 || res.status === 408) {
          throw new Error('AI request timed out (the server took longer than 26 seconds). Try again — sometimes the API is slower under load. If it persists, shorten the customer story.');
        }
        if (res.status >= 500) {
          throw new Error(`Server error (${res.status}). The AI request likely timed out or hit an upstream issue — try again. If it persists, shorten the customer story or contact admin.`);
        }
        throw new Error(`Unexpected response from AI service (HTTP ${res.status}). The server returned content that wasn't JSON — likely an error page.`);
      }
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setAiResult(data.result);
    } catch (e) {
      alert('AI generation failed: ' + e.message);
    }
    setGenerating(false);
  }

  const autoInclusions = buildAutoInclusions({ inputs, pkg, selectedProductObjects });
  const complianceDeclinations = buildComplianceDeclinations({ inputs, products, complianceKey });

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', display:'flex', alignItems:'stretch', justifyContent:'flex-end', zIndex:600 }}>
      <div style={{ flex:1 }} onClick={onClose} />
      <div style={{ width:700, background:'white', display:'flex', flexDirection:'column', boxShadow:'-8px 0 40px rgba(0,0,0,0.2)' }}>
        {/* Header */}
        <div style={{ background:'#0f1e3c', padding:'14px 20px', display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
          <div>
            <div style={{ fontSize:15, fontWeight:700, color:'white' }}>Assumptions & Exclusions</div>
            <div style={{ fontSize:10, color:'#64748b', marginTop:1 }}>{clientName || 'Client'} · {quoteNumber || 'Unsaved quote'}</div>
          </div>
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            {saveMsg && <span style={{ fontSize:11, fontWeight:600, color: saveMsg.startsWith('✓') ? '#4ade80' : '#f87171' }}>{saveMsg}</span>}
            <button onClick={save} disabled={saving}
              style={{ padding:'6px 16px', background:'#f97316', color:'white', border:'none', borderRadius:5, fontSize:12, fontWeight:700, cursor:'pointer', opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button onClick={onClose} style={{ background:'none', border:'none', color:'#64748b', fontSize:22, cursor:'pointer', lineHeight:1 }}>×</button>
          </div>
        </div>

        <div style={{ flex:1, overflowY:'auto' }}>

          {/* ── ENVIRONMENT & USER ASSUMPTIONS ── */}
          <Sec title="Environment & User Assumptions" sub="Used for pricing — provided during discovery" color="#0f1e3c">
            <Field label="Customer Story / Discovery Notes">
              <textarea value={story} onChange={e => setStory(e.target.value)} rows={5}
                placeholder="Type or paste your raw discovery notes here — everything you learned on the call. Who are they, what do they do, what problems are they having, what do they need? The AI will translate this into clean professional assumptions." style={TA} />
              <div style={{ display:'flex', justifyContent:'flex-end', alignItems:'center', gap:10, marginTop:6 }}>
                {/* v3.5.81.8: helper text explaining the disabled state.
                    The previous render gave reps no feedback on why the
                    button was greyed out; multiple reps reported "the AI
                    button is broken" when in fact they hadn't typed
                    a story yet. */}
                {!story.trim() && !generating && (
                  <span style={{ fontSize:10, color:'#9ca3af', fontStyle:'italic' }}>
                    Type a story above to enable AI generation
                  </span>
                )}
                <button onClick={generateAssumptions} disabled={generating || !story.trim()}
                  style={{ padding:'5px 14px', background: generating ? '#9ca3af' : '#7c3aed', color:'white', border:'none', borderRadius:4, fontSize:11, fontWeight:700, cursor: (generating || !story.trim()) ? 'not-allowed' : 'pointer', display:'flex', alignItems:'center', gap:5 }}>
                  {generating ? (
                    <>
                      <span style={{ display:'inline-block', width:10, height:10, border:'2px solid rgba(255,255,255,0.3)', borderTopColor:'white', borderRadius:'50%', animation:'spin 0.7s linear infinite' }}/>
                      Generating...
                    </>
                  ) : '✨ Generate Assumptions with AI'}
                </button>
              </div>
            </Field>

            {/* AI-generated assumptions preview */}
            {aiResult && (
              <div style={{ background:'#faf5ff', border:'1px solid #e9d5ff', borderRadius:6, padding:'12px 14px', marginTop:4 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                  <div style={{ fontSize:11, fontWeight:700, color:'#7c3aed' }}>✨ AI-Generated Assumptions</div>
                  <button onClick={() => setAiResult(null)}
                    style={{ background:'none', border:'none', color:'#9ca3af', cursor:'pointer', fontSize:12 }}>Clear</button>
                </div>

                {/* v3.5.81.17: Recommendation Summary — placed first because
                    it's the section the prospect reads BEFORE pricing on the
                    proposal cover, and it's the connective tissue between
                    "you said this" and "you'll pay this." Made editable
                    inline (textarea, not <p>) because this is the section
                    most worth a rep tightening to match buyer-specific
                    voice. Edits flow back into aiResult via setAiResult. */}
                {aiResult.recommendationSummary !== undefined && (
                  <div style={{ marginBottom:14, padding:'10px 12px', background:'white', border:'1px solid #d8b4fe', borderRadius:5 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
                      <div style={{ fontSize:9, fontWeight:700, color:'#6d28d9', textTransform:'uppercase', letterSpacing:'.04em' }}>Our Recommendation (shown to client)</div>
                      <span style={{ fontSize:9, color:'#9ca3af', fontStyle:'italic' }}>Editable</span>
                    </div>
                    <textarea
                      value={aiResult.recommendationSummary || ''}
                      onChange={e => setAiResult(r => ({ ...r, recommendationSummary: e.target.value }))}
                      rows={6}
                      style={{ width:'100%', fontSize:11, color:'#374151', lineHeight:1.7, padding:'8px 10px', border:'1px solid #e9d5ff', borderRadius:4, fontFamily:'inherit', resize:'vertical', boxSizing:'border-box' }}
                    />
                  </div>
                )}

                {/* v3.5.81.23: Solution Groups editor — the "middle altitude"
                    section that bridges from the high-level recommendation
                    to the granular line items. Each group bundles 2-5
                    products into one named outcome (e.g. "Layered Endpoint
                    Protection") with an emoji icon, narrative paragraph,
                    and product chip list. Reps can edit name/narrative
                    inline, remove a group with the × button, or regenerate
                    the whole AI block to start over. lineItemNames is
                    intentionally NOT user-editable here — the AI grounds
                    those names in the actual selected products list, and
                    letting reps edit them invites typos that break the
                    chip rendering on the proposal. If a chip needs to
                    change, the rep should regenerate. */}
                {Array.isArray(aiResult.solutionGroups) && aiResult.solutionGroups.length > 0 && (
                  <div style={{ marginBottom:14, padding:'10px 12px', background:'white', border:'1px solid #d8b4fe', borderRadius:5 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                      <div style={{ fontSize:9, fontWeight:700, color:'#6d28d9', textTransform:'uppercase', letterSpacing:'.04em' }}>Your Solution — outcome blocks (shown to client)</div>
                      <span style={{ fontSize:9, color:'#9ca3af', fontStyle:'italic' }}>Editable · {aiResult.solutionGroups.length} {aiResult.solutionGroups.length === 1 ? 'group' : 'groups'}</span>
                    </div>
                    {aiResult.solutionGroups.map((g, idx) => (
                      <div key={idx} style={{ marginBottom:10, padding:'8px 10px', background:'#fafafa', border:'1px solid #e5e7eb', borderRadius:4 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                          <span style={{ fontSize:14 }}>{g.icon || '•'}</span>
                          <input
                            value={g.name || ''}
                            onChange={e => setAiResult(r => {
                              const groups = [...(r.solutionGroups || [])];
                              groups[idx] = { ...groups[idx], name: e.target.value };
                              return { ...r, solutionGroups: groups };
                            })}
                            style={{ flex:1, fontSize:11, fontWeight:700, color:'#0f1e3c', padding:'4px 6px', border:'1px solid #e5e7eb', borderRadius:3, fontFamily:'inherit' }}
                          />
                          <button
                            onClick={() => setAiResult(r => ({ ...r, solutionGroups: (r.solutionGroups || []).filter((_, i) => i !== idx) }))}
                            title="Remove this group"
                            style={{ background:'none', border:'none', color:'#9ca3af', cursor:'pointer', fontSize:14, padding:'0 4px' }}>×</button>
                        </div>
                        <textarea
                          value={g.narrative || ''}
                          onChange={e => setAiResult(r => {
                            const groups = [...(r.solutionGroups || [])];
                            groups[idx] = { ...groups[idx], narrative: e.target.value };
                            return { ...r, solutionGroups: groups };
                          })}
                          rows={3}
                          style={{ width:'100%', fontSize:11, color:'#374151', lineHeight:1.6, padding:'6px 8px', border:'1px solid #e5e7eb', borderRadius:3, fontFamily:'inherit', resize:'vertical', boxSizing:'border-box', marginBottom:5 }}
                        />
                        {Array.isArray(g.lineItemNames) && g.lineItemNames.length > 0 && (
                          <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
                            <span style={{ fontSize:9, color:'#6b7280', marginRight:2, alignSelf:'center' }}>Includes:</span>
                            {g.lineItemNames.map((name, i) => (
                              <span key={i} style={{ fontSize:9, padding:'2px 7px', background:'#e5e7eb', color:'#374151', borderRadius:8 }}>{name}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                    <div style={{ fontSize:9, color:'#9ca3af', fontStyle:'italic', marginTop:4 }}>
                      Product chips reflect what the AI mapped to each group. To change a chip list, click Generate again.
                    </div>
                  </div>
                )}

                {aiResult.environmentSummary && (
                  <div style={{ marginBottom:10 }}>
                    <div style={{ fontSize:9, fontWeight:700, color:'#6d28d9', textTransform:'uppercase', letterSpacing:'.04em', marginBottom:3 }}>Environment Overview</div>
                    <p style={{ fontSize:11, color:'#374151', lineHeight:1.7, margin:0 }}>{aiResult.environmentSummary}</p>
                  </div>
                )}

                {aiResult.switchingContext && (
                  <div style={{ marginBottom:10 }}>
                    <div style={{ fontSize:9, fontWeight:700, color:'#6d28d9', textTransform:'uppercase', letterSpacing:'.04em', marginBottom:3 }}>Why FerrumIT</div>
                    <p style={{ fontSize:11, color:'#374151', lineHeight:1.7, margin:0 }}>{aiResult.switchingContext}</p>
                  </div>
                )}

                {[
                  ['User Assumptions', aiResult.userAssumptions],
                  ['Infrastructure Assumptions', aiResult.infrastructureAssumptions],
                  ['Cloud & Technology', aiResult.cloudAssumptions],
                  ['Key Requirements', aiResult.keyRequirements],
                ].filter(([, items]) => items?.length > 0).map(([label, items]) => (
                  <div key={label} style={{ marginBottom:10 }}>
                    <div style={{ fontSize:9, fontWeight:700, color:'#6d28d9', textTransform:'uppercase', letterSpacing:'.04em', marginBottom:4 }}>{label}</div>
                    {items.map((item, i) => (
                      <div key={i} style={{ display:'flex', gap:6, marginBottom:2 }}>
                        <span style={{ color:'#7c3aed', fontSize:10, flexShrink:0, marginTop:1 }}>•</span>
                        <span style={{ fontSize:11, color:'#374151', lineHeight:1.6 }}>{item}</span>
                      </div>
                    ))}
                  </div>
                ))}

                {aiResult.complianceNotes && (
                  <div style={{ marginTop:6, padding:'6px 10px', background:'#fef3c7', borderRadius:4, fontSize:10, color:'#92400e' }}>
                    ⚠ {aiResult.complianceNotes}
                  </div>
                )}

                <div style={{ fontSize:9, color:'#9ca3af', marginTop:8, fontStyle:'italic' }}>
                  This is saved with the quote and will appear in the formatted assumptions document. Edit the fields above to refine further.
                </div>
              </div>
            )}
            <Grid2>
              <Field label="Current Provider / IT Setup">
                <input value={currentProvider} onChange={e => setCurrentProvider(e.target.value)}
                  placeholder="e.g., CDW, local MSP, internal IT..." style={IN} />
              </Field>
              <Field label="Why are they switching? (add context beyond the checkboxes)">
                <textarea value={switchDetails} onChange={e => setSwitchDetails(e.target.value)} rows={2}
                  placeholder="e.g., Slow response times, unresolved tickets for months, no proactive monitoring, staff constantly calling with the same issues..." style={TA} />
              </Field>
              <Field label="Remote / Hybrid Workers">
                <div style={{ display:'flex', gap:6 }}>
                  <input type="number" min="0" value={remoteCount} onChange={e => setRemoteCount(+e.target.value)}
                    style={{ ...IN, width:60, fontFamily:'DM Mono, monospace', fontWeight:600 }} />
                  <select value={remoteType} onChange={e => setRemoteType(e.target.value)} style={{ ...IN, flex:1 }}>
                    <option value="hybrid">Hybrid</option>
                    <option value="fully_remote">Fully Remote</option>
                    <option value="occasional">Occasional</option>
                  </select>
                </div>
              </Field>
            </Grid2>

            {/* v3.5.86.1: Supported Users / Power Users + Task Workers panel
                are IT-specific — both reference inputs.users (the count of
                billable Power Users in IT pricing). Multi-Site will get its
                own location-aware variant in v3.5.86.2. */}
            {(quoteType === 'it' || quoteType === 'bundle') && (
              <>
                <Field label={`Supported Users / Power Users (${inputs?.users || 0} included in pricing)`}>
                  <div style={{ fontSize:9, color:'#6b7280', marginBottom:4 }}>
                    List names or roles of employees included in the managed services pricing. One per line.
                    If additional employees are added who require computers, email, or application support, they will be added at the standard per-user rate.
                  </div>
                  <textarea value={userList} onChange={e => setUserList(e.target.value)} rows={6}
                    placeholder={`e.g.\nJohn Smith\nJane Doe\nFinance Team (5 users)\n...`} style={TA} />
                </Field>

                <div style={{ background:'#fafafa', border:'1px solid #f1f5f9', borderRadius:5, padding:'10px 12px', marginTop:8 }}>
                  <div style={{ fontSize:11, fontWeight:700, color:'#374151', marginBottom:2 }}>Task Workers vs Power Users</div>
                  <div style={{ fontSize:9, color:'#6b7280', marginBottom:8 }}>
                    Task workers have limited IT needs (tablets, job logging, no email/desktop) and are excluded from Power User pricing.
                    Power users are the {inputs?.users || 0} users included in the quoted price.
                  </div>
                  <Grid2>
                    <Field label="Number of task workers (not in Power User count)">
                      <input value={taskWorkerCount} onChange={e => setTaskWorkerCount(e.target.value)}
                        placeholder="e.g., 8–10" style={IN} />
                    </Field>
                    <div />
                  </Grid2>
                  <Field label="What do task workers use? Why excluded?">
                    <textarea value={fieldTechNotes} onChange={e => setFieldTechNotes(e.target.value)} rows={3}
                      placeholder="e.g., Use tablets to log field work activity only. No desktop computers, printers, or company email accounts. If they require email, laptops, or IT support they will be added at the applicable per-user rate." style={TA} />
                  </Field>
                </div>
              </>
            )}
          </Sec>

          {/* v3.5.86.2: Multi-Site Configuration capture. Renders only for
              quoteType === 'multi_site'. The locations themselves come from
              the MultiSiteQuotePage (passed via inputs.locations); this
              section captures context that ISN'T in the locations array —
              HQ designation, inter-site connectivity, and regional/per-site
              notes. The AI prompt in assumptionsAI.js consumes these in
              the multi_site branch of buildUserMessage. */}
          {quoteType === 'multi_site' && (
            <Sec title="Multi-Site Configuration" sub="Per-location context the AI uses to ground multi-site assumptions" color="#6d28d9">
              <Field label="Headquarters Location">
                <select value={hqLocationId} onChange={e => setHqLocationId(e.target.value)} style={IN}>
                  <option value="">— No HQ designated (fully distributed) —</option>
                  {(Array.isArray(inputs?.locations) ? inputs.locations : []).map((loc, i) => {
                    // Use loc.id when present, fall back to a positional key.
                    // Locations array shape comes from createLocation() in pricing.js;
                    // existing locations may have an id or tempId field.
                    const key = loc.id || loc.tempId || `loc_${i}`;
                    const label = loc.name || `Location ${i + 1}`;
                    const place = [loc.city, loc.state].filter(Boolean).join(', ');
                    return (
                      <option key={key} value={key}>
                        {label}{place ? ` — ${place}` : ''}
                      </option>
                    );
                  })}
                </select>
                <div style={{ fontSize:9, color:'#6b7280', marginTop:4 }}>
                  When set, the AI will treat this site as HQ and apply
                  HQ-style assumptions (shared infrastructure, identity,
                  primary network) — branches get site-local assumptions
                  instead. Leave unset for engagements where every site is
                  equivalent.
                </div>
              </Field>
              <Field label="Inter-Site Connectivity">
                <textarea value={connectivityNotes} onChange={e => setConnectivityNotes(e.target.value)} rows={3}
                  placeholder="e.g., MPLS between HQ and branch offices, SD-WAN failover. Phoenix office on separate ISP, no VPN tunnel today. Or: each site has its own internet connection, no dedicated inter-site links." style={TA} />
              </Field>
              <Field label="Regional / Per-Location Notes">
                <textarea value={regionalNotes} onChange={e => setRegionalNotes(e.target.value)} rows={4}
                  placeholder="e.g., California office subject to CCPA — needs additional data-handling considerations. NYC location runs 24x7 reception so help desk needs after-hours coverage there. Phoenix is the only site with an on-prem server room. Anything that varies per-location goes here." style={TA} />
              </Field>
            </Sec>
          )}

          {/* v3.5.86.1: Core Infrastructure / Cloud / Modernization are
              IT-specific. Multi-Site has its own capture section above
              (v3.5.86.2). v3.5.86.3 widens the gate to include 'bundle' —
              Bundle quotes now render these IT sections plus a Voice
              addendum below. Voice STANDALONE has its own VoiceAssumptionsModal. */}
          {(quoteType === 'it' || quoteType === 'bundle') && (
            <>
              {/* ── CORE INFRASTRUCTURE ── */}
              <Sec title="Core Infrastructure in Scope" sub="Discovered during the sales call" color="#7c3aed">
                <Field label="Laptops & Workstations">
                  <textarea value={laptopNotes} onChange={e => setLaptopNotes(e.target.value)} rows={2}
                    placeholder="e.g., Mix of Dell and HP desktops, 3-5 years old. 4 MacBooks used by design team. Most running Windows 11." style={TA} />
                </Field>
                <Field label="Servers">
                  <textarea value={serverNotes} onChange={e => setServerNotes(e.target.value)} rows={3}
                    placeholder="e.g., Windows Server 2016 (on-premises rack server) — currently used for Active Directory and file storage. Microsoft support ends October 2027." style={TA} />
                </Field>
                <Field label="Network & Security (firewalls, switches, routers, failover)">
                  <textarea value={networkNotes} onChange={e => setNetworkNotes(e.target.value)} rows={3}
                    placeholder="e.g., Cisco Firepower 1000 Series, Cradlepoint device (cellular failover), network switches supporting internal LAN, Arris gateway..." style={TA} />
                </Field>
                <Field label="Telephony & Communication">
                  <textarea value={telephonyNotes} onChange={e => setTelephonyNotes(e.target.value)} rows={2}
                    placeholder="e.g., Digium G100 telephony gateway, Sangoma Fax Station..." style={TA} />
                </Field>
              </Sec>

              {/* ── CLOUD SERVICES ── */}
              <Sec title="Cloud Services" color="#0891b2">
                <Grid2>
                  <Field label="Microsoft 365">
                    <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
                      <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer', fontSize:11 }}>
                        <input type="checkbox" checked={m365Managed} onChange={e => setM365Managed(e.target.checked)} />
                        We are managing M365 licensing
                      </label>
                      {m365Managed && <input value={m365Tier} onChange={e => setM365Tier(e.target.value)} placeholder="License tier (e.g., Business Premium)" style={{ ...IN, fontSize:10 }} />}
                    </div>
                  </Field>
                  <Field label="M365 Usage (what they use it for)">
                    <textarea value={m365Notes} onChange={e => setM365Notes(e.target.value)} rows={3}
                      placeholder="Email, Collaboration, Office applications..." style={TA} />
                  </Field>
                </Grid2>
                <Field label="Other Cloud / SaaS Applications">
                  <textarea value={cloudNotes} onChange={e => setCloudNotes(e.target.value)} rows={2}
                    placeholder="e.g., Most line-of-business applications appear to be cloud-hosted, reducing reliance on on-premises infrastructure..." style={TA} />
                </Field>
              </Sec>

              {/* ── MODERNIZATION OPPORTUNITIES ── */}
              <Sec title="Infrastructure Modernization Opportunities" sub="Identified but NOT included in base pricing" color="#d97706">
                <div style={{ fontSize:9, color:'#92400e', marginBottom:6 }}>
                  These are improvements identified during discovery that are outside the base managed services scope. They would be handled as separate projects if pursued.
                </div>
                <textarea value={modernizationNotes} onChange={e => setModernizationNotes(e.target.value)} rows={4}
                  placeholder="e.g., Retirement or migration of Windows Server 2016 environment&#10;Identity migration to cloud-based authentication&#10;Network architecture modernization&#10;Telephony platform upgrades or SIP migration" style={TA} />
              </Sec>
            </>
          )}

          {/* v3.5.86.3: Voice Configuration capture for Bundle quotes. Renders
              only when quoteType === 'bundle'. Light-touch capture compared to
              VoiceAssumptionsModal — seat counts, DIDs, fax requirements come
              from the quote inputs themselves (passed via inputs.voiceSeats,
              inputs.voiceDids, etc.), so this section only collects context the
              quote engine doesn't know:
                - Voice incumbent (separate from IT incumbent — often a carrier)
                - Why switching voice (separate pain points from IT switch)
                - Current PBX type (cloud / onprem / hybrid / none)
                - Voice hardware notes (handset state, BYOH plans)
                - Voice network / QoS notes (bandwidth, VLAN, codec readiness)
                - Number porting plan (full / partial / all-new) */}
          {quoteType === 'bundle' && (
            <Sec title="Voice Configuration" sub="Voice-side discovery — combined with IT capture above for the AI prompt" color="#2563eb">
              <Grid2>
                <Field label="Current Voice Provider">
                  <input type="text" value={voiceCurrentProvider} onChange={e => setVoiceCurrentProvider(e.target.value)}
                    placeholder="e.g., Spectrum Business Voice, RingCentral, on-prem Avaya"
                    style={IN} />
                </Field>
                <Field label="Current PBX Type">
                  <select value={pbxType} onChange={e => setPbxType(e.target.value)} style={IN}>
                    <option value="">— Select —</option>
                    <option value="cloud">Cloud-hosted (RingCentral / 8x8 / Vonage / etc.)</option>
                    <option value="onprem">On-premises PBX (Avaya / Mitel / NEC / etc.)</option>
                    <option value="hybrid">Hybrid (cloud + on-prem)</option>
                    <option value="none">No formal phone system today</option>
                  </select>
                </Field>
              </Grid2>
              <Field label="Why Switching Voice?">
                <textarea value={voiceSwitchReasons} onChange={e => setVoiceSwitchReasons(e.target.value)} rows={2}
                  placeholder="e.g., Contract expiring with current carrier. Tired of paying per-extension. Want unified billing with IT. PBX EOL." style={TA} />
              </Field>
              <Field label="Voice Hardware Notes">
                <textarea value={voiceHardwareNotes} onChange={e => setVoiceHardwareNotes(e.target.value)} rows={2}
                  placeholder="e.g., 25 Polycom VVX 411s, 4 years old, want to keep. OR: existing handsets are EOL, want all new. OR: BYOH — client owns Yealink T46G phones." style={TA} />
              </Field>
              <Field label="Voice Network / QoS Notes">
                <textarea value={voiceNetworkNotes} onChange={e => setVoiceNetworkNotes(e.target.value)} rows={2}
                  placeholder="e.g., 200/200 Mbps fiber, VLAN segmentation in place. OR: shared LAN with no QoS configured — needs review during onboarding. Bandwidth: ~100 kbps per concurrent call." style={TA} />
              </Field>
              <Field label="Number Porting Plan">
                <textarea value={portingNotes} onChange={e => setPortingNotes(e.target.value)} rows={2}
                  placeholder="e.g., Porting 25 existing DIDs from Spectrum, including main number 813-555-0100. OR: All new numbers — no port. OR: Partial — keep main line, replace direct extensions." style={TA} />
              </Field>
            </Sec>
          )}

          {/* ── DELIVERABLES ── */}
          <Sec title="Deliverables" color="#166534">
            <div style={{ fontSize:9, color:'#6b7280', marginBottom:5 }}>Pre-populated with standard deliverables language. Edit as needed for this client.</div>
            <textarea value={deliverables} onChange={e => setDeliverables(e.target.value)} rows={8} style={TA} />
          </Sec>

          {/* v3.5.86.1: Quoted Services and Compliance Declinations are
              IT-specific. Both source from `pkg`, `inputs.users`,
              `inputs.workstations`, and `products[].compliance_tags` — all
              fields scoped to IT pricing. Multi-Site / Bundle will get
              equivalent (but quote-type-flavored) sections in v3.5.86.2/.3. */}
          {(quoteType === 'it' || quoteType === 'bundle') && (
            <>
              {/* ── INCLUSIONS (auto-generated) ── */}
              <Sec title="Quoted Services — What IS Included" color="#166534">
                <div style={{ fontSize:9, color:'#6b7280', marginBottom:8, fontStyle:'italic' }}>Auto-generated from your quote selections.</div>
                {autoInclusions.map((item, i) => (
                  <div key={i} style={{ display:'flex', gap:6, padding:'4px 8px', borderRadius:3, marginBottom:2, background:'#f0fdf4', border:'1px solid #bbf7d0' }}>
                    <span style={{ color:'#16a34a', fontSize:10, flexShrink:0 }}>✓</span>
                    <span style={{ fontSize:11, color:'#166534' }}>{item}</span>
                  </div>
                ))}
                {m365Managed && (
                  <div style={{ display:'flex', gap:6, padding:'4px 8px', borderRadius:3, marginBottom:2, background:'#f0fdf4', border:'1px solid #bbf7d0' }}>
                    <span style={{ color:'#16a34a', fontSize:10, flexShrink:0 }}>✓</span>
                    <span style={{ fontSize:11, color:'#166534' }}>Microsoft 365 {m365Tier ? `(${m365Tier}) ` : ''}licensing management</span>
                  </div>
                )}
              </Sec>
            </>
          )}

          {/* ── EXCLUSIONS ── */}
          <Sec title="Exclusions — What Is NOT Included" color="#dc2626">
            <div style={{ fontSize:9, color:'#6b7280', marginBottom:8, fontStyle:'italic' }}>Standard exclusions always included. Add any client-specific exclusions below.</div>
            {STANDARD_EXCLUSIONS.map((text, i) => (
              <div key={i} style={{ display:'flex', gap:6, padding:'4px 8px', borderRadius:3, marginBottom:2, background:'#fef2f2', border:'1px solid #fecaca' }}>
                <span style={{ color:'#dc2626', fontSize:10, flexShrink:0 }}>•</span>
                <span style={{ fontSize:11, color:'#991b1b' }}>{text}</span>
              </div>
            ))}
            {customExclusions.map(item => (
              <div key={item.id} style={{ display:'flex', gap:6, padding:'4px 8px', borderRadius:3, marginBottom:2, background:'#fef3c7', border:'1px solid #fde68a', alignItems:'center' }}>
                <span style={{ color:'#d97706', fontSize:10, flexShrink:0 }}>•</span>
                <span style={{ fontSize:11, color:'#92400e', flex:1 }}>{item.text}</span>
                <button onClick={() => setCustomExclusions(p => p.filter(e => e.id !== item.id))}
                  style={{ background:'none', border:'none', color:'#9ca3af', cursor:'pointer', fontSize:12, padding:0 }}>×</button>
              </div>
            ))}
            <div style={{ display:'flex', gap:6, marginTop:8 }}>
              <input value={newExclusion} onChange={e => setNewExclusion(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addExclusion()}
                placeholder="Add a client-specific exclusion..." style={{ ...IN, flex:1 }} />
              <button onClick={addExclusion} disabled={!newExclusion.trim()}
                style={{ padding:'5px 12px', background:'#0f1e3c', color:'white', border:'none', borderRadius:4, fontSize:11, cursor:'pointer', opacity: newExclusion.trim() ? 1 : 0.4 }}>Add</button>
            </div>
          </Sec>

          {/* ── COMPLIANCE DECLINATIONS ── */}
          {(quoteType === 'it' || quoteType === 'bundle') && complianceDeclinations.length > 0 && (
            <Sec title="Compliance Recommendations — Client Declinations" color="#d97706">
              <div style={{ fontSize:10, color:'#92400e', marginBottom:8, lineHeight:1.5 }}>
                The following services were recommended based on the client's compliance requirements but were declined. This is documented to protect FerrumIT and provide a clear record of informed decision-making.
              </div>
              {complianceDeclinations.map((item, i) => (
                <div key={i} style={{ padding:'8px 12px', borderRadius:5, marginBottom:6, background:'#fffbeb', border:'1px solid #fde68a' }}>
                  <div style={{ fontSize:11, fontWeight:700, color:'#92400e' }}>⚠ {item.product}</div>
                  {item.reason && <div style={{ fontSize:10, color:'#78350f', marginTop:3, lineHeight:1.5 }}>{item.reason}</div>}
                  <div style={{ fontSize:9, color:'#a16207', marginTop:4, fontStyle:'italic' }}>
                    Client has been advised and is declining this service. This declination is documented in the signed agreement.
                  </div>
                </div>
              ))}
            </Sec>
          )}

          {/* ── ADDITIONAL NOTES ── */}
          <Sec title="Additional Notes" color="#374151">
            <textarea value={customNotes} onChange={e => setCustomNotes(e.target.value)} rows={3}
              placeholder="Any additional context or special arrangements for this client..." style={TA} />
          </Sec>

        </div>
      </div>
    </div>
  );
}

function buildAutoInclusions({ inputs, pkg, selectedProductObjects }) {
  if (!inputs || !pkg) return [];
  const lines = [];
  lines.push(`Managed IT services under the ${pkg.name} package covering ${inputs.users || 0} user${inputs.users !== 1 ? 's' : ''} and ${inputs.workstations || 0} workstation${inputs.workstations !== 1 ? 's' : ''}`);
  if (inputs.servers > 0) lines.push(`Server management for ${inputs.servers} server${inputs.servers !== 1 ? 's' : ''}`);
  if (inputs.locations > 1) lines.push(`Coverage across ${inputs.locations} locations`);
  for (const p of selectedProductObjects) lines.push(p.name);
  const coverageMap = { business_hours: 'Business hours (Mon–Fri 8am–5pm)', '24x5': '24×5', '24x7': '24×7' };
  if (coverageMap[inputs.requestedCoverage]) lines.push(`${coverageMap[inputs.requestedCoverage]} help desk coverage`);
  return lines;
}

function buildComplianceDeclinations({ inputs, products, complianceKey }) {
  if (!complianceKey?.length || !products) return [];
  const selectedIds = inputs?.selectedProducts || [];
  return products
    .filter(p => p.compliance_tags?.some(t => complianceKey.includes(t)) && !selectedIds.includes(p.id) && p.active)
    .map(p => ({
      product: p.name,
      reason: p.recommendation_reason || `Recommended for ${p.compliance_tags?.filter(t => complianceKey.includes(t)).map(t => COMPLIANCE_LABELS[t]).join('/')} compliance`,
    }));
}

// v3.5.86.1: AI payload builder, keyed by quoteType. Each branch produces
// the JSON body for the assumptionsAI Netlify function. The function ignores
// the `quoteType` field for now — v3.5.86.2 lights up routing when Multi-Site
// lands. The `quoteType` field is sent regardless so the foundation is in
// place and a future deploy is a one-line backend change.
//
// IT case: identical to the v3.5.81.8 payload (the bug-fixed version that
// uses the actual `switchDetails` state var instead of the typo'd
// `switchReasons`). Behavior change for IT users: NONE.
//
// multi_site / bundle: throw a pointed error naming the upcoming deploy slot.
// This is foundation-only — those quoteTypes are not yet mountable on any
// QuotePage, so this branch is currently unreachable. If a future developer
// (or future me) wires a mount before .2/.3 ship, the rep will see a clear
// alert telling them which deploy is needed instead of an opaque AI failure.
function buildAIPayload(quoteType, state, props) {
  const { story, switchDetails, currentProvider,
          serverNotes, networkNotes, laptopNotes, m365Notes, cloudNotes,
          remoteCount, remoteType, taskWorkerCount, fieldTechNotes } = state;
  const { clientName, inputs, pkg, selectedProductObjects } = props;

  // Universal fields — sent for every quoteType. Backend can use these as
  // common context regardless of the quote type-specific extras below.
  const universal = {
    quoteType,
    story,
    clientName,
    currentProvider,
    switchDetails,
    remoteCount,
    remoteType,
  };

  if (quoteType === 'it') {
    return {
      ...universal,
      userCount:        inputs?.users,
      workstationCount: inputs?.workstations,
      serverNotes, networkNotes, laptopNotes, m365Notes, cloudNotes,
      selectedProducts: selectedProductObjects.map(p => p.name),
      compliance:       inputs?.compliance,
      industryRisk:     inputs?.industryRisk,
      packageName:      pkg?.name,
      taskWorkerCount,
      taskWorkerNotes:  fieldTechNotes,
    };
  }

  if (quoteType === 'multi_site') {
    // v3.5.86.2: Multi-Site payload. Surfaces per-location detail (locations[]),
    // HQ designation, inter-site connectivity, and regional notes — all read by
    // the multi_site branch of buildUserMessage in assumptionsAI.js. The IT-only
    // capture fields (server/network/laptop notes, M365, task workers) are
    // intentionally NOT sent — they're hidden in the UI for multi_site quotes
    // and would only confuse the per-location framing.
    return {
      ...universal,
      locations:        Array.isArray(inputs?.locations) ? inputs.locations : [],
      hqLocationId:     state.hqLocationId      || '',
      connectivityNotes: state.connectivityNotes || '',
      regionalNotes:    state.regionalNotes     || '',
      // Aggregated counts for the AI to size the engagement at-a-glance.
      // The per-location breakdown is in `locations[]`.
      userCount:        inputs?.users,
      workstationCount: inputs?.workstations,
      compliance:       inputs?.compliance,
      industryRisk:     inputs?.industryRisk,
      packageName:      pkg?.name,
      // selectedProducts is universal-but-IT-flavored — included so the
      // solutionGroups section of the response can ground product chips
      // against the actual catalog. Multi-Site quotes use the same product
      // catalog as IT.
      selectedProducts: selectedProductObjects.map(p => p.name),
    };
  }

  if (quoteType === 'bundle') {
    // v3.5.86.3: Bundle (IT+Voice combined) payload. Combines IT-side
    // capture (server/network/laptop notes, M365, task workers — same shape
    // as the IT branch) with voice-side capture (current voice provider, why
    // switching voice, PBX type, hardware/network/porting notes — collected
    // by the Voice Configuration section that renders only for bundle).
    //
    // Voice seat count, DID count, fax type, and contract term come from
    // the QUOTE INPUTS, not the modal — bundle quotes already capture
    // these in BundleQuotePage's voice/IT input panels. Passed via
    // `inputs.voiceSeats`, `inputs.voiceDids`, `inputs.voiceFaxType`,
    // `inputs.contractTerm`. The voice tier label comes through similarly
    // (`inputs.voiceTier`).
    return {
      ...universal,
      // IT-side context (mirrors the 'it' branch above).
      userCount:        inputs?.users,
      workstationCount: inputs?.workstations,
      serverNotes, networkNotes, laptopNotes, m365Notes, cloudNotes,
      compliance:       inputs?.compliance,
      industryRisk:     inputs?.industryRisk,
      packageName:      pkg?.name,
      taskWorkerCount,
      taskWorkerNotes:  state.fieldTechNotes,
      // Voice-side context — sourced from the bundle's voice inputs (counts,
      // tier, fax type, term) and the modal's voice capture fields (provider,
      // switch reasons, PBX, notes, porting).
      voiceTier:            inputs?.voiceTier,
      voiceSeats:           inputs?.voiceSeats,
      voiceDids:            inputs?.voiceDids,
      voiceFaxType:         inputs?.voiceFaxType,
      contractTerm:         inputs?.contractTerm,
      voiceCurrentProvider: state.voiceCurrentProvider || '',
      voiceSwitchReasons:   state.voiceSwitchReasons   || '',
      pbxType:              state.pbxType              || '',
      voiceHardwareNotes:   state.voiceHardwareNotes   || '',
      voiceNetworkNotes:    state.voiceNetworkNotes    || '',
      portingNotes:         state.portingNotes         || '',
      // Combined product list — both IT and voice products surface here so
      // the AI can build solutionGroups spanning both surfaces. The page
      // is responsible for passing the union via selectedProductObjects.
      selectedProducts:     selectedProductObjects.map(p => p.name),
    };
  }

  throw new Error(
    `Unknown quoteType "${quoteType}" passed to AssumptionsModal. ` +
    `Expected one of: it, multi_site, bundle.`
  );
}

// Helpers
const TA = { width:'100%', padding:'6px 8px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, resize:'vertical', outline:'none', lineHeight:1.6 };
const IN = { width:'100%', padding:'5px 8px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, outline:'none' };
function Sec({ title, sub, color, children }) {
  return (
    <div style={{ padding:'14px 20px', borderBottom:'1px solid #f1f5f9' }}>
      <div style={{ marginBottom:10 }}>
        <div style={{ fontSize:12, fontWeight:700, color, display:'inline' }}>{title}</div>
        {sub && <div style={{ fontSize:10, color:'#9ca3af', marginTop:1, fontStyle:'italic' }}>{sub}</div>}
      </div>
      {children}
    </div>
  );
}
function Field({ label, children }) {
  return (
    <div style={{ marginBottom:10 }}>
      <label style={{ display:'block', fontSize:9, fontWeight:700, color:'#374151', marginBottom:3, textTransform:'uppercase', letterSpacing:'.04em' }}>{label}</label>
      {children}
    </div>
  );
}
function Grid2({ children }) { return <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>{children}</div>; }
